import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { LOCAL_STORAGE_KEYS } from 'src/app/shared/constants/local-storage.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { GetUserRegions } from 'src/app/shared/store/states/user/user.actions';
import { Observable, switchMap, tap } from 'rxjs';
import { UserState } from 'src/app/shared/store/states/user/user.state';
import { UserService } from 'src/app/shared/services/user.service';
import { AsyncPipe } from '@angular/common';
import { RegionDto } from 'src/app/shared/dtos/user.dto';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TermsAndConditionsModal } from 'src/app/core/components/modals/terms-and-conditions-modal/terms-and-conditions-modal';
import { RegionModal } from 'src/app/core/components/modals/region-modal/region-modal';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.html',
  styleUrl: './settings.scss',
  imports: [
    AsyncPipe,
    TranslocoPipe
  ]
})
export class Settings implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly transloco = inject(TranslocoService);

  private ref: DynamicDialogRef | null;
  protected currentRegion: RegionDto | undefined;

  protected username$: Observable<string> = this.userService.getCurrentUsername().pipe(
    tap(username => {
      return username;
    })
  );

  ngOnInit(): void {
    this.store.dispatch(new GetUserRegions()).pipe(
      switchMap(() => this.store.select(UserState.getUserRegions)),
      tap(regions => {
        this.currentRegion = regions.find(region => region.current);
      }),
    ).subscribe();

    this.analyticsService.analyticsPageData = {
      itemName: 'settings',
      itemId: 'settings'
    }

    this.analyticsService.trackPageLoad();
  }

  protected openTermsAndConditionsDialog(): void {
    this.ref = this.dialogService.open(TermsAndConditionsModal, {
      header: this.transloco.translate('settings.termsAndConditions'),
      focusOnShow: false,
      closable: true,
      dismissableMask: true,
      modal: true,
      width: '50%',
      breakpoints: {
        '1400px': '50%',
        '960px': '75%',
        '640px': '90%'
      },
    });
  }

  protected openRegionsDialog(): void {
    this.ref = this.dialogService.open(RegionModal, {
      header: this.transloco.translate('settings.selectRegion'),
      focusOnShow: false,
      closable: true,
      modal: true,
      width: '30%',
      breakpoints: {
        '1400px': '50%',
        '960px': '75%',
        '640px': '90%'
      },
    });

    this.ref?.onClose.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (!this.currentRegion) {
        return;
      }

      this.localStorageService.setItem(LOCAL_STORAGE_KEYS.InitialRegion, this.currentRegion);
    });
  }
}
